import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, AlertTriangle, Settings, Gift } from 'lucide-react';
import { Database, HardDrive } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { useEnhancedCompany } from '../../contexts/EnhancedCompanyContext';
import { auth } from '../../firebase.config';
import { diagnoseSubscription, fixMissingSubscription } from '../../utils/subscriptionDiagnostics';
import { useNotification } from '../../hooks/useNotification';
import { 
  PlanHeader, 
  TopupCard, 
  UsageSection, 
  SubscriptionActions,
  USAGE_METRICS 
} from './SubscriptionComponents';

const SubscriptionStatusModern = ({ db }) => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  const notify = useNotification();
  
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [fixing, setFixing] = useState(false);

  const loadSubscription = useCallback(async () => {
    if (!companyId || !db) {
      console.log('🔍 Missing companyId or db');
      return;
    }

    try {
      setLoading(true);
      console.log('🔍 Loading subscription for:', companyId);
      
      const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
      const subscriptionSnap = await getDoc(subscriptionRef);

      if (subscriptionSnap.exists()) {
        const data = subscriptionSnap.data();
        console.log('✅ Subscription loaded');
        
        // Debug: Log date fields to troubleshoot validity calculation
        const endDateRaw = data.endDate;
        const nextBillingDateRaw = data.basePlan?.nextBillingDate;
        const nextResetDateRaw = data.basePlan?.nextResetDate;
        console.log('📅 Subscription date fields:', {
          endDate: endDateRaw,
          endDateType: endDateRaw ? (endDateRaw.toDate ? 'Timestamp' : typeof endDateRaw) : 'null',
          nextBillingDate: nextBillingDateRaw,
          nextBillingDateType: nextBillingDateRaw ? (nextBillingDateRaw.toDate ? 'Timestamp' : typeof nextBillingDateRaw) : 'null',
          nextResetDate: nextResetDateRaw,
          nextResetDateType: nextResetDateRaw ? (nextResetDateRaw.toDate ? 'Timestamp' : typeof nextResetDateRaw) : 'null',
          billing: data.basePlan?.billing,
          validityDays: data.basePlan?.validityDays
        });
        
        setSubscription(data);
      } else {
        console.log('❌ No subscription found');
        setSubscription(null);
      }
    } catch (error) {
      console.error('❌ Error loading subscription:', error);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, db]);

  useEffect(() => {
    if (companyId && db) {
      loadSubscription();
    }
  }, [companyId, db, loadSubscription]);

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      date = new Date(dateValue);
    }
    
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateDaysRemaining = (dateValue) => {
    if (!dateValue) return 0;
    
    try {
      let date;
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        date = new Date(dateValue);
      }
      
      if (isNaN(date.getTime())) return 0;
      
      const now = new Date();
      const diffTime = date - now;
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, days);
    } catch (error) {
      return 0;
    }
  };

  const handleDiagnostics = async () => {
    if (!companyId || !db) return;
    
    setShowDiagnostics(true);
    const result = await diagnoseSubscription(db, companyId);
    setDiagnosticResults(result);
  };

  const handleFixSubscription = async () => {
    if (!companyId || !db) return;
    
    const userId = auth.currentUser?.uid;
    if (!userId) {
      notify.error('User not authenticated');
      return;
    }

    setFixing(true);
    const result = await fixMissingSubscription(db, companyId, userId);
    setFixing(false);

    if (result.success) {
      notify.success('Subscription created successfully!', { title: 'Success' });
      await loadSubscription();
    } else {
      notify.error('Failed to create subscription: ' + result.error, { title: 'Error' });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading subscription...</p>
        </div>
      </div>
    );
  }

  // No subscription state
  if (!subscription) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-10 h-10 text-yellow-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Subscription Found</h3>
            <p className="text-gray-600 mb-6">
              Your company should have been assigned a subscription automatically.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleFixSubscription}
                disabled={fixing}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-6 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {fixing ? (
                  <>
                    <RefreshCw className="inline w-5 h-5 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>🔧 Create Subscription Now</>
                )}
              </button>
              
              <button
                onClick={handleDiagnostics}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Run Diagnostics
              </button>
            </div>

            {showDiagnostics && diagnosticResults && (
              <div className="mt-6 p-4 bg-gray-50 rounded-xl text-left border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2">Diagnostic Results:</h4>
                <pre className="text-xs text-gray-700 overflow-auto max-h-64 whitespace-pre-wrap">
                  {JSON.stringify(diagnosticResults, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Extract data
  const basePlan = subscription.basePlan || subscription;
  const topups = (subscription.topups || []).filter(t => t.status === 'active');
  const totalEntitlements = subscription.totalEntitlements || basePlan.limits || {};
  const usage = subscription.currentMonthUsage || {};
  
  // Get endDate - try multiple sources for compatibility
  // Priority: root endDate > basePlan.nextBillingDate > basePlan.nextResetDate > trialEndDate
  let endDate = null;
  if (basePlan.isTrial) {
    endDate = basePlan.trialEndDate || basePlan.nextBillingDate || basePlan.nextResetDate;
  } else {
    // For non-trial, prefer nextBillingDate (especially for yearly plans) over nextResetDate
    endDate = subscription.endDate || 
              basePlan.nextBillingDate || 
              basePlan.nextResetDate;
  }
  
  // Debug: Log the selected endDate and calculated days
  if (endDate) {
    const endDateConverted = endDate.toDate ? endDate.toDate() : (endDate instanceof Date ? endDate : new Date(endDate));
    const now = new Date();
    const days = Math.ceil((endDateConverted.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    console.log('📅 Validity calculation:', {
      endDateSource: subscription.endDate ? 'root.endDate' : (basePlan.nextBillingDate ? 'basePlan.nextBillingDate' : 'basePlan.nextResetDate'),
      endDateValue: endDateConverted.toISOString(),
      now: now.toISOString(),
      daysRemaining: days,
      billing: basePlan.billing
    });
  }
  
  const daysRemaining = calculateDaysRemaining(endDate);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Plan Header */}
        <PlanHeader 
          plan={basePlan}
          daysRemaining={daysRemaining}
          endDate={endDate}
          formatDate={formatDate}
        />

        {/* Active Top-ups */}
        {topups.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">Active Top-ups</h3>
                <p className="text-sm text-gray-500">Boost your limits</p>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-bold">
                {topups.length} Active
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {topups.map((topup, index) => (
                <TopupCard key={index} topup={topup} />
              ))}
            </div>
          </div>
        )}

        {/* Usage Section */}
        <UsageSection 
          usage={usage}
          entitlements={totalEntitlements}
          onRefresh={loadSubscription}
        />

        {/* Action Buttons */}
        <SubscriptionActions />

        <p className="text-center pt-2">
          <Link
            to="/usage-statistics"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            View usage statistics (monthly & yearly report)
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SubscriptionStatusModern;





























